import { publishedConfigVersion } from '@de/events/stepCorrelation';
import type { ConfiguredEventContext } from './types';

/**
 * Integer config version of at least 1. Anything else is absent,
 * including `0` and a non-integer.
 */
function readDslConfigVersion(version: unknown): number | undefined {
  return publishedConfigVersion(version);
}

function presentString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function authoredString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * `pageCategory` and `contentGroup` from `data.root.props.tracking`.
 * Anything else is absent. These are not the step-correlation category.
 */
export function readRootExperienceTracking(data: unknown): {
  readonly pageCategory?: string;
  readonly contentGroup?: string;
} {
  if (!isPlainObject(data)) return {};
  const root = data.root;
  if (!isPlainObject(root)) return {};
  const props = root.props;
  if (!isPlainObject(props)) return {};
  const tracking = props.tracking;
  if (!isPlainObject(tracking)) return {};
  const pageCategory = authoredString(tracking.pageCategory);
  const contentGroup = authoredString(tracking.contentGroup);
  return {
    ...(pageCategory ? { pageCategory } : {}),
    ...(contentGroup ? { contentGroup } : {}),
  };
}

/**
 * Step-level experience values. `blockId` and `blockType` come from
 * the node at resolve time. `pageCategory` and `contentGroup` are
 * included only when the caller supplies them.
 */
export function buildConfiguredEventContext(input: {
  readonly dslConfigId?: string;
  readonly dslConfigVersion?: unknown;
  readonly flowId?: string;
  readonly stepId?: string;
  readonly stepIndex?: number;
  readonly pageCategory?: string;
  readonly contentGroup?: string;
}): ConfiguredEventContext {
  const dslConfigId = presentString(input.dslConfigId);
  const dslConfigVersion = readDslConfigVersion(input.dslConfigVersion);
  const flowId = presentString(input.flowId);
  const stepId = presentString(input.stepId);
  const pageCategory = presentString(input.pageCategory);
  const contentGroup = presentString(input.contentGroup);
  const stepIndex =
    typeof input.stepIndex === 'number' &&
    Number.isInteger(input.stepIndex) &&
    input.stepIndex >= 0
      ? input.stepIndex
      : undefined;
  return {
    ...(dslConfigId ? { dslConfigId } : {}),
    ...(dslConfigVersion !== undefined ? { dslConfigVersion } : {}),
    ...(flowId ? { flowId } : {}),
    ...(stepId ? { stepId } : {}),
    ...(stepIndex !== undefined ? { stepIndex } : {}),
    ...(pageCategory ? { pageCategory } : {}),
    ...(contentGroup ? { contentGroup } : {}),
  };
}
